import { roundMoney } from '@/format';
import type { ReceiptLine } from './ItemLines';

/**
 * Each line of a stock-in names its own supplier, store, bin, vehicle, invoice
 * and remarks — one stock-in can bring several suppliers' goods on several
 * vehicles. The delivery date is the receipt's own: one for every line. These
 * are the rules that keep that from meaning six extra boxes to fill on every
 * line.
 */

/** A supplier typed on a line that is registered when the receipt is saved. */
const NEW_SUPPLIER = 'new:';

export function newSupplierValue(name: string): string {
  return `${NEW_SUPPLIER}${name.trim()}`;
}

/** The typed name of a supplier still to be registered, else null. */
export function newSupplierName(supplierId: string): string | null {
  return supplierId.startsWith(NEW_SUPPLIER) ? supplierId.slice(NEW_SUPPLIER.length) : null;
}

/** The delivery details a line can copy, change, or hand on to lines below. */
export type DeliveryField = 'supplier' | 'store' | 'bin' | 'vehicleNo' | 'invoiceRef' | 'remarks';

export const DELIVERY_FIELDS: readonly DeliveryField[] = [
  'supplier',
  'store',
  'bin',
  'vehicleNo',
  'invoiceRef',
  'remarks',
];

/** A line counts once it has an item or a quantity — the blank rows the form
 *  opens with do not, so they never fill up with copied details. */
export function isStarted(line: ReceiptLine): boolean {
  return Boolean(line.skuId || line.qty.trim());
}

export function fieldValue(line: ReceiptLine, field: DeliveryField): string {
  switch (field) {
    case 'supplier':
      return line.supplierId;
    case 'store':
      return line.storeId;
    case 'bin':
      return line.binId;
    default:
      return line[field];
  }
}

/** What copying a field from `line` writes. A bin belongs to its store, so
 *  the store travels with its bin. */
function fieldPatch(line: ReceiptLine, field: DeliveryField): Partial<ReceiptLine> {
  switch (field) {
    case 'supplier':
      return { supplierId: line.supplierId, supplierLabel: line.supplierLabel };
    case 'store':
    case 'bin':
      return { storeId: line.storeId, binId: line.binId, binLabel: line.binLabel };
    default:
      return { [field]: line[field] };
  }
}

/** The nearest line above `index` that has been started, if any. */
function startedAbove(lines: readonly ReceiptLine[], index: number): ReceiptLine | undefined {
  for (let i = index - 1; i >= 0; i -= 1) {
    if (isStarted(lines[i]!)) return lines[i];
  }
  return undefined;
}

export interface DeliveryDefaults {
  /** For the first line, which has nothing above it to copy. */
  storeId: string;
}

/**
 * Fills the delivery details a line has just been started with, from the
 * started line above it — or, for the first line, the defaults. Only boxes
 * still empty are filled, so nothing anybody chose is overwritten.
 */
function carryForward(lines: readonly ReceiptLine[], index: number, defaults: DeliveryDefaults): ReceiptLine {
  const line = lines[index]!;
  const above = startedAbove(lines, index);
  const patch: Partial<ReceiptLine> = {};
  if (above) {
    if (!line.supplierId) Object.assign(patch, fieldPatch(above, 'supplier'));
    if (!line.storeId) Object.assign(patch, fieldPatch(above, 'store'));
    for (const field of ['vehicleNo', 'invoiceRef', 'remarks'] as const) {
      if (!line[field]) patch[field] = above[field];
    }
  } else if (!line.storeId && defaults.storeId) {
    patch.storeId = defaults.storeId;
  }
  return Object.keys(patch).length ? { ...line, ...patch } : line;
}

/**
 * Applies carry-forward to every started line not yet filled — once per line,
 * so a box the receiver empties afterwards stays empty. Lines are taken top
 * to bottom, so several started at once each copy the one above, which has
 * already copied its own.
 *
 * Keyed on the line's own flag rather than on what `prev` held: one pick can
 * report the same line twice, the second time from before the first was
 * filled, and that must not undo the copy.
 */
export function startLines(
  _prev: readonly ReceiptLine[],
  next: ReceiptLine[],
  defaults: DeliveryDefaults,
): ReceiptLine[] {
  let out = next;
  next.forEach((line, i) => {
    if (!isStarted(line) || line.carried) return;
    const filled = { ...carryForward(out, i, defaults), carried: true };
    out = out === next ? [...next] : out;
    out[i] = filled;
  });
  return out;
}

/**
 * The vehicle numbers a line's box offers: those on this receipt first, so a
 * truck carrying several items can be picked again before it has been saved
 * anywhere, then the ones already on file. One entry per plate, however it was
 * cased or spaced when typed, as the server lists them.
 */
export function offeredVehicles(lines: readonly ReceiptLine[], onFile: readonly string[]): string[] {
  const byPlate = new Map<string, string>();
  for (const value of [...lines.map((l) => l.vehicleNo), ...onFile]) {
    const plate = value.trim();
    const key = plate.toUpperCase().replace(/\s+/g, '');
    if (key && !byPlate.has(key)) byPlate.set(key, plate);
  }
  return [...byPlate.values()];
}

/** Whether the line shows the same value as the started line above it, so a
 *  change of supplier or vehicle stands out from the lines that only repeat. */
export function sameAsAbove(lines: readonly ReceiptLine[], index: number, field: DeliveryField): boolean {
  const value = fieldValue(lines[index]!, field);
  if (!value) return false;
  const above = startedAbove(lines, index);
  return above !== undefined && fieldValue(above, field) === value;
}

/**
 * The lines straight below `index` still showing `oldValue` for `field` —
 * those that copied it from this line before it was changed. Stops at the
 * first started line that differs, since one that differs was set on purpose.
 */
export function linesBelowWith(
  lines: readonly ReceiptLine[],
  index: number,
  field: DeliveryField,
  oldValue: string,
): string[] {
  const keys: string[] = [];
  for (let i = index + 1; i < lines.length; i += 1) {
    const line = lines[i]!;
    if (!isStarted(line)) continue;
    if (fieldValue(line, field) !== oldValue) break;
    keys.push(line.key);
  }
  return keys;
}

/** Copies `field` from the line at `index` onto the lines named. */
export function applyBelow(
  lines: readonly ReceiptLine[],
  index: number,
  field: DeliveryField,
  keys: readonly string[],
): ReceiptLine[] {
  const patch = fieldPatch(lines[index]!, field);
  const targets = new Set(keys);
  return lines.map((l) => (targets.has(l.key) ? { ...l, ...patch } : l));
}

export interface SupplierGroup {
  supplierId: string;
  supplierLabel: string;
  lines: ReceiptLine[];
  invoiceRefs: string[];
  vehicleNos: string[];
  /** Quantity × cost summed, to the paisa; null while no line has a cost. */
  total: number | null;
}

/** The started lines by supplier, in the order each first appears — what is
 *  owed to whom. */
export function supplierGroups(lines: readonly ReceiptLine[]): SupplierGroup[] {
  const groups = new Map<string, SupplierGroup>();
  for (const line of lines) {
    if (!isStarted(line)) continue;
    const group =
      groups.get(line.supplierId) ??
      ({
        supplierId: line.supplierId,
        supplierLabel: line.supplierLabel,
        lines: [],
        invoiceRefs: [],
        vehicleNos: [],
        total: null,
      } satisfies SupplierGroup);
    group.lines.push(line);
    const invoice = line.invoiceRef.trim();
    if (invoice && !group.invoiceRefs.includes(invoice)) group.invoiceRefs.push(invoice);
    const vehicle = line.vehicleNo.trim();
    if (vehicle && !group.vehicleNos.includes(vehicle)) group.vehicleNos.push(vehicle);
    if (line.qty.trim() && line.unitCost.trim()) {
      const value = Number(line.qty) * Number(line.unitCost);
      if (Number.isFinite(value)) group.total = roundMoney((group.total ?? 0) + roundMoney(value));
    }
    groups.set(line.supplierId, group);
  }
  return [...groups.values()];
}

/** How many different non-empty values the started lines have for `field`. */
export function distinctCount(lines: readonly ReceiptLine[], field: DeliveryField): number {
  return new Set(lines.filter(isStarted).map((l) => fieldValue(l, field).trim()).filter(Boolean)).size;
}
